import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BugDetailsDrawer = ({ bug, isOpen, onClose, onAction }) => {
  // ✅ Track local status for immediate UI feedback
  const [localStatus, setLocalStatus] = useState("");

  useEffect(() => {
    if (bug) {
      setLocalStatus(bug.status || "unresolved");
    }
  }, [bug]);

  if (!bug) return null;

  const handleStatusChange = (newStatus) => {
    setLocalStatus(newStatus);
    onAction("updateStatus", bug.id, newStatus);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-[#0B1C2D]/30 backdrop-blur-sm z-[1100]"
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            className="fixed top-0 right-0 h-[100dvh] w-full sm:w-[550px] bg-white shadow-2xl z-[1101] flex flex-col font-sans"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {/* HEADER */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <span
                  className={`px-2 py-1 rounded text-[10px] font-black uppercase ${bug.severity === "Critical" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}
                >
                  {bug.severity}
                </span>
                <h2 className="text-xl font-black text-[#0B1C2D] mt-2">
                  {bug.title}
                </h2>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              <section>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                  Steps to Reproduce
                </h4>
                <div className="bg-slate-50 p-4 rounded-2xl text-sm text-[#0B1C2D] border border-slate-100">
                  {bug.stepsToReproduce}
                </div>
              </section>

              <div className="grid grid-cols-2 gap-4">
                <section>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Expected</h4>
                  <p className="text-xs text-green-600 font-bold">{bug.expectedBehavior}</p>
                </section>
                <section>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Actual</h4>
                  <p className="text-xs text-red-600 font-bold">{bug.actualResult}</p>
                </section>
              </div>

              <section className="bg-slate-900 rounded-[24px] p-6 text-white shadow-xl">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Device Metadata</h4>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-[10px] font-bold text-white/50">ENVIRONMENT</span>
                    <span className="text-[11px] font-mono">{bug.environment}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-[10px] font-bold text-white/50">RESOLUTION</span>
                    <span className="text-[11px] font-mono">{bug.deviceInfo?.screenResolution}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold text-white/50 uppercase">User Agent</span>
                    <span className="text-[10px] font-mono bg-white/5 p-3 rounded-xl break-all leading-relaxed">
                      {bug.deviceInfo?.userAgent}
                    </span>
                  </div>
                </div>
              </section>
            </div>

            {/* ✅ REDESIGNED STATUS UPDATE CONTAINER */}
            <div className="p-6 border-t border-gray-100 bg-white space-y-4">
              <div className="flex items-center justify-between px-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Update Lifecycle</h4>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${localStatus === 'resolved' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="text-[10px] font-black uppercase text-[#0B1C2D]">{localStatus}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-1 flex bg-slate-100 p-1 rounded-2xl">
                  {["unresolved", "resolved"].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={`flex-1 py-3 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all ${
                        localStatus === status
                          ? "bg-white text-[#0B1C2D] shadow-sm"
                          : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => {
                    onAction("delete", bug.id);
                    onClose();
                  }}
                  className="px-6 py-4 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-2xl text-[10px] font-black uppercase transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BugDetailsDrawer;